<template>
    <div class="card">
        <Toolbar class="p-mb-4" v-if="!nested">
            <template slot="left">
                <Button label="New" icon="pi pi-plus" class="p-button-success p-m-1" @click="createDocument"/>
                <Button label="Delete" icon="pi pi-trash" class="p-button-danger p-m-1"
                        @click="confirmDeleteSelectedDocuments(selectedDocuments)"
                        :disabled="!selectedDocuments || !selectedDocuments.length"/>
            </template>
            <template slot="right" v-if="adminMode">
                <Button label="Verify" icon="pi pi-check" class="p-button-help p-m-1"
                        @click="toggleDocuments(selectedDocuments, true)"
                        :disabled="!selectedDocuments || !selectedDocuments.length"/>
                <Button label="Unverify" icon="pi pi-times" class="p-button-warning p-m-1"
                        @click="toggleDocuments(selectedDocuments, false)"
                        :disabled="!selectedDocuments || !selectedDocuments.length"/>
            </template>
        </Toolbar>

        <DataTable ref="dt" :value="documents" :selection.sync="selectedDocuments" dataKey="_jv.id"
                   :loading="loading"
                   :paginator="paginator"
                   class="p-datatable-customers"
                   :rows="10" :filters="filters"
                   paginatorTemplate="FirstPageLink PrevPageLink PageLinks NextPageLink LastPageLink CurrentPageReport RowsPerPageDropdown"
                   :rowsPerPageOptions="[5,10,25]"
                   currentPageReportTemplate="Showing {first} to {last} of {totalRecords} Document Qualifications"
                   :expandedRows.sync="expandedRows"
        >
            <Column v-if="!nested" selectionMode="multiple" headerStyle="width: 3rem"></Column>
            <Column v-if="adminMode" field="is-admin" header="Project / General" sortable filterMatchMode="contains"
                    headerStyle="width: 10%">
                <template #body="slotProps">
                    <span :class="`customer-badge status-${slotProps.data['is-admin'] ? 'warning' : 'success'}`">
                        {{ slotProps.data['is-admin'] ? 'Project' : 'General' }}
                    </span>
                </template>
                <template #filter v-if="filter">
                    <MultiSelect v-model="filters['is-admin']"
                                 :options="[{key: 0, value: 'General'}, {key: 1, value: 'Project'}]"
                                 optionLabel="value" optionValue="key" placeholder="Choose one" style="width: 100%">
                        <template #option="slotProps">
                            <div>{{ slotProps.option.value }}</div>
                        </template>
                    </MultiSelect>
                </template>
            </Column>
            <Column v-if="adminMode" field="verified" header="Status" sortable filterMatchMode="contains"
                    headerStyle="width: 10%">
                <template #body="slotProps">
                    <span v-if="slotProps.data['verified']"
                          class="customer-badge status-success"
                          v-tooltip="`Verified by: ${staffs.find(staff => staff._jv.id == slotProps.data['verified-by']).name}\nVerified at: ${$moment(slotProps.data['verified-at']).format('YYYY-MM-DD')}`">
                        Verified
                    </span>
                    <span v-else class="customer-badge status-danger"> Unverified </span>
                </template>
                <template #filter v-if="filter">
                    <MultiSelect v-model="filters['verified']"
                                 :options="[{key: 0, value: 'Unverified'}, {key: 1, value: 'Verified'}]"
                                 optionLabel="value" optionValue="key" placeholder="Choose one" style="width: 100%">
                        <template #option="slotProps">
                            <div>{{ slotProps.option.value }}</div>
                        </template>
                    </MultiSelect>
                </template>
            </Column>
            <Column field="name" header="Name" sortable filterMatchMode="contains">
                <template #filter v-if="filter">
                    <InputText type="text" v-model="filters['name']" placeholder="Search by Name"
                               style="width: 100%"/>
                </template>
            </Column>
            <Column field="description" header="Description" sortable filterMatchMode="contains">
                <template #filter v-if="filter">
                    <InputText type="text" v-model="filters['description']" placeholder="Search by Description"
                               style="width: 100%"/>
                </template>
            </Column>
            <Column field="updated-at" header="Updated On (After)" sortable sortField="updated-at"
                    filterMatchMode="custom" :filterFunction="filterDate">
                <template #body="slotProps">
                    {{ $formatDate(slotProps.data['updated-at']) }}
                </template>
                <template #filter v-if="filter">
                    <Calendar v-model="filters['updated-at']" view="month" dateFormat="mm/yy"
                              :yearNavigator="true" :yearRange="`1950:${new Date().getFullYear()}`"
                              placeholder="MM/YYYY"/>
                </template>
            </Column>
            <Column v-if="showStaff" field="staff.name" header="Staff" sortable filterMatchMode="contains">
                <template #filter v-if="filter">
                    <InputText type="text" v-model="filters['staff.name']" placeholder="Search by Staff"
                               style="width: 100%"/>
                </template>
            </Column>
            <Column>
                <template #body="slotProps" v-if="!nested">
                    <Button icon="pi pi-pencil" class="p-button-rounded p-button-success p-mr-2"
                            @click="editDocument(slotProps.data)"/>
                    <Button icon="pi pi-trash" class="p-button-rounded p-button-warning p-mr-2"
                            @click="confirmDeleteDocument(slotProps.data)"/>
                    <span class="p-overlay-badge">
                        <Button icon="pi pi-file" class="p-button-rounded p-button-info p-mr-2"
                                @click="showFileDialog(slotProps.data)"/>
                        <span v-if="slotProps.data._jv.relationships.files.meta.total"
                              class="p-badge p-badge-danger">
                            {{ slotProps.data._jv.relationships.files.meta.total }}
                        </span>
                    </span>
                </template>
            </Column>
            <template #empty>
                No records found.
            </template>
        </DataTable>
    </div>
</template>

<script>
import {Component, Prop, Emit, Vue, Watch} from 'vue-property-decorator'
import Accordion from 'primevue/accordion';
import AccordionTab from 'primevue/accordiontab';
import Toolbar from 'primevue/toolbar';
import DataTable from 'primevue/datatable';
import Column from 'primevue/column';
import Button from 'primevue/button';
import InputText from 'primevue/inputtext';
import MultiSelect from 'primevue/multiselect';
import Calendar from 'primevue/calendar';
import StaffService from "../../service/StaffService";

/**
 * Class-component
 */
@Component({
    components: {
        Accordion: Vue.extend(Accordion),
        AccordionTab: Vue.extend(AccordionTab),
        Toolbar: Vue.extend(Toolbar),
        DataTable: Vue.extend(DataTable),
        Column: Vue.extend(Column),
        Button: Vue.extend(Button),
        InputText: Vue.extend(InputText),
        MultiSelect: Vue.extend(MultiSelect),
        Calendar: Vue.extend(Calendar),
    }
})
export default class DocumentDataTable extends Vue {
    @Prop(Array) documents;
    @Prop({type: Boolean, default: false}) nested;
    @Prop({type: Boolean, default: false}) paginator;
    @Prop({type: Boolean, default: false}) filter;
    @Prop({type: Boolean, default: false}) adminMode;
    @Prop(Boolean) showStaff;
    @Prop(Boolean) loading;

    @Emit() createDocument() {
        this.$emit('createDocument')
    }

    @Emit() confirmDeleteSelectedDocuments(documents) {
        this.$emit('confirmDeleteSelectedDocuments', documents)
    }

    @Emit() toggleDocuments(documents) {
        this.$emit('toggleDocuments', documents)
    }

    @Emit() editDocument(document) {
        this.$emit('editDocument', document)
    }

    @Emit() confirmDeleteDocument(document) {
        this.$emit('confirmDeleteDocument', document)
    }

    @Emit() showFileDialog(document) {
        this.$emit('showFileDialog', document)
    }

    selectedDocuments = [];
    expandedRows = [];
    filters = {};
    staffService = new StaffService();
    staffs = [];

    async mounted() {
        this.staffs = await this.staffService.getStaffs();
    }

    // custom filter date
    filterDate(value, filter) {
        return filter instanceof Date ? this.$moment(value).isSameOrAfter(this.$moment(filter)) : value.includes(filter);
    }
}
</script>

<style lang="scss" scoped>
.customer-badge {
    border-radius: 5px;
    padding: .25em .5rem;
    text-transform: uppercase;
    font-weight: 700;
    font-size: 12px;
    letter-spacing: .3px;

    &.status-success {
        background: #C8E6C9;
        color: #256029;
    }

    &.status-danger {
        background: #FFCDD2;
        color: #C63737;
    }

    &.status-warning {
        background: #FFD8B2;
        color: #805B36;
    }
}

/deep/ .p-progressbar {
    height: .5rem;
    background-color: #D8DADC;

    .p-progressbar-value {
        background-color: #607D8B;
    }
}

.table-header {
    display: flex;
    justify-content: space-between;
}

/deep/ .p-datepicker {
    min-width: 25rem;

    td {
        font-weight: 400;
    }
}

/deep/ .p-datatable.p-datatable-customers {
    .p-datatable-header {
        padding: 1rem;
        text-align: left;
        font-size: 1.5rem;
    }

    .p-paginator {
        padding: 1rem;
    }

    .p-datatable-thead > tr > th {
        text-align: left;
    }

    .p-datatable-tbody > tr > td {
        cursor: auto;
    }

    .p-dropdown-label:not(.p-placeholder) {
        text-transform: uppercase;
    }
}

/* Responsive */
.p-datatable-customers .p-datatable-tbody > tr > td .p-column-title {
    display: none;
}

@media screen and (max-width: 960px) {
    /deep/ .p-datatable {
        &.p-datatable-customers {
            .p-datatable-thead > tr > th,
            .p-datatable-tfoot > tr > td {
                display: none !important;
            }

            .p-datatable-tbody > tr {
                border-bottom: 1px solid var(--layer-2);

                > td {
                    text-align: left;
                    display: block;
                    border: 0 none !important;
                    width: 100% !important;
                    float: left;
                    clear: left;
                    border: 0 none;

                    .p-column-title {
                        padding: .4rem;
                        min-width: 30%;
                        display: inline-block;
                        margin: -.4rem 1rem -.4rem -.4rem;
                        font-weight: bold;
                    }

                    .p-progressbar {
                        margin-top: .5rem;
                    }
                }
            }
        }
    }
}
</style>
